import Honeybadger from '@honeybadger-io/js'
import { NetworkError } from '../errors'
import { userAuthHeader } from '../apiClient'
import { newRequestId, fetchOrThrowNetworkError } from './utils'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import type { TEntityId } from '~/types/models'

/**
 * Recovering a create whose RESPONSE was lost.
 *
 * A `NetworkError` on a POST does not mean the POST failed. The connection can
 * drop after the server has committed the row and before the 201 reaches the
 * browser — production, 2026-10-26 (Honeybadger 135220454): the API's own
 * `http_request_log` recorded `POST /api/products` → 201 in 0.23s, the browser
 * reported "Could not reach the server" 0.4s later, and the product existed.
 * The dialog told the user the create had failed, closed, and never refreshed
 * the list — so the natural next step, entering it again, makes a DUPLICATE:
 * `postEntityData` mints a fresh id on every save, so nothing collides.
 *
 * What makes recovery possible is that the CLIENT mints the id
 * (`getNextEntityId`). So after a transport failure we can simply ask whether a
 * row with that id exists: if it does, the create landed and its row is the
 * answer the POST would have given.
 *
 * Deliberately narrow — see `lostCreateRecoveryPath`.
 */

/**
 * A plain collection path, `/api/products` or `/api/opportunities/`. Only for
 * these is `<path>/<id>` the GET for the row just POSTed. Sub-resource POSTs
 * (`/api/cases/<id>/replies`, `/merges`, `/resend`) and `/overview` creates are
 * excluded: appending an id to them names something else, or nothing.
 */
const COLLECTION_PATH = /^\/api\/[A-Za-z]+\/?$/

/** Delays before each lookup. The first is immediate; a blip that dropped the
 * POST's response can still be in progress, so later tries wait it out. */
export const LOOKUP_DELAYS_MS = [0, 1000, 2000]

/**
 * The GET path that would find the row this POST created, or undefined when
 * the POST is not one we can recover.
 *
 * An async POST (202 + polling) is excluded because it already carries an
 * Idempotency-Key, which is the right tool there; a caller-built multipart body
 * because those are exactly the sub-resource endpoints above.
 */
export function lostCreateRecoveryPath({
  apiPath,
  entity,
  isAsync,
  hasCallerBuiltBody,
}: {
  apiPath: string
  entity: unknown
  isAsync: boolean
  hasCallerBuiltBody: boolean
}): string | undefined {
  if (isAsync || hasCallerBuiltBody) {
    return undefined
  }
  if (!COLLECTION_PATH.test(apiPath)) {
    return undefined
  }
  if (!entity || typeof entity !== 'object' || Array.isArray(entity)) {
    return undefined
  }
  const id = (entity as { id?: unknown }).id
  if (typeof id !== 'string' || !id) {
    return undefined
  }
  return `${apiPath.replace(/\/$/, '')}/${encodeURIComponent(id)}`
}

/**
 * Look the row up, and return it when the create turns out to have landed.
 * Returns undefined for "could not confirm", and the caller then rethrows the
 * original NetworkError, exactly as before this existed.
 *
 * ⚠ A raw fetch, NOT `httpGet`. `httpGet` treats a 403 as an expired session
 * and logs the user out; a recovery probe must have no side effects beyond the
 * request itself. Any non-200 answer is just "not confirmed".
 */
export async function recoverLostCreate({
  fn,
  recoveryPath,
  entityId,
  networkError,
  sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
}: {
  fn: string
  recoveryPath: string
  entityId: TEntityId
  networkError: NetworkError
  sleep?: (ms: number) => Promise<unknown>
}): Promise<unknown | undefined> {
  for (const delay of LOOKUP_DELAYS_MS) {
    if (delay) {
      await sleep(delay)
    }
    let response: Response
    try {
      response = await fetchOrThrowNetworkError(recoveryPath, {
        method: 'GET',
        headers: Object.assign(
          {
            'x-request-id': newRequestId(),
            'x-app-version': getPublicRuntimeConfig().version,
          },
          userAuthHeader()
        ),
      })
    } catch (e) {
      // Still unreachable: try again after the next delay.
      console.warn(`${fn} lost-create lookup could not reach the server -`, e)
      continue
    }
    if (!response.ok) {
      // A definite answer. 404 = the create did not land (or not yet); anything
      // else means this path cannot tell us. Either way, not confirmed.
      console.warn(
        `${fn} lost-create lookup GET ${recoveryPath} → ${response.status};` +
          ' the create is not confirmed.'
      )
      return undefined
    }
    let row: unknown
    try {
      row = (await response.json())?.data
    } catch {
      return undefined
    }
    if ((row as { id?: unknown } | undefined)?.id !== entityId) {
      console.warn(
        `${fn} lost-create lookup GET ${recoveryPath} returned a different row.`
      )
      return undefined
    }
    console.warn(
      `${fn} the POST's response was lost, but GET ${recoveryPath} found the` +
        ' row: the create landed. Continuing as a success.'
    )
    reportRecoveredCreate({ recoveryPath, entityId, networkError })
    return row
  }
  return undefined
}

/**
 * Kept as a Honeybadger fault on purpose (Jerry, 2026-10-26), so the first one
 * alerts Slack and the rest are countable. The message is FIXED, with the path
 * in context, so every recovery groups into one fault rather than one per
 * entity.
 */
function reportRecoveredCreate({
  recoveryPath,
  entityId,
  networkError,
}: {
  recoveryPath: string
  entityId: TEntityId
  networkError: NetworkError
}) {
  Honeybadger.notify(
    'A create whose response was lost was found on the server and recovered.',
    {
      name: 'LostCreateResponseRecovered',
      context: {
        recoveryPath,
        entityId,
        originalError: networkError.message,
        cause: String(networkError.cause),
      },
    }
  )
}
